import { Fragment, useState, useEffect, MouseEventHandler } from "react";
import styled from "@emotion/styled";
import useMedia from "react-use/lib/useMedia";
import ReactCountryFlag from "react-country-flag";
import { Drawer, EXPANDED_WIDTH } from "./Drawer";
import { NavLink, NavItemDivider } from "./NavLink";
import { NavDropdown } from "./NavDropdown";
import { TopAppBar, TopAppBarProps } from "./TopAppBar";
import { Typography } from "../Typography";
import { CountryChangeModal } from "../CountryChangeModal";
import { Location } from "@ecologi/shared";
import { Link } from "../Link";
import { COMPRESS_HEADER_BP } from "./Hamburger";
import { NavSection } from "./navigation";

const ContentContainerInner = styled.main`
  width: 100%;
`;

const ContentContainer = styled.div<{ isLeft: boolean }>`
  z-index: 99;
  width: 100%;
  padding-left: ${({ isLeft }) => (isLeft ? `${EXPANDED_WIDTH(false)}px` : 0)};

  @media ${COMPRESS_HEADER_BP} {
    padding-left: ${({ isLeft }) => (isLeft ? `${EXPANDED_WIDTH(true)}px` : 0)};
  }
`;

const Root = styled.div`
  display: -webkit-box;
  min-height: 100vh;
`;

const Navigation = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  margin: 56px 0px 34px 20px;
  &::after {
    content: "";
    background-image: url("/images/app/nav/logo.svg");
    background-repeat: no-repeat;
    background-size: 100%;
    height: 51px;
    width: 100px;
  }
`;

export const CountryWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  height: 30px;
  padding: 34px 24px;
`;

export const CountrySelect = styled.a<{ isLight: boolean | undefined }>`
  border-radius: 3px;
  padding: 3px;
  font-size: 24px;
  text-decoration: none;
  transition: background 250ms ease-in;

  &:hover {
    background: ${({ isLight, theme }) =>
      isLight
        ? theme.colors.opacity(theme.colors.snowdrop, 0.15)
        : theme.colors.opacity(theme.colors.night, 0.05)};
  }

  span {
    display: inline-block;
    background-repeat: no-repeat;
    background-size: cover;
    height: 16px;
    width: 24px;
    @media ${(props) => props.theme.bp.mobileUp} {
      height: 20px;
      width: 30px;
    }
  }
`;

export type NavigationDrawerProps = {
  navSections: NavSection[];
  /**
   * The main page content
   */
  children: React.ReactNode;
  /**
   * The navigation drawer has two modes. Either as a fixed position overlay
   * or as left side bar which is in the flow of the page.
   */
  isOverlayNav: boolean;
  /**
   * The root nav item which is selected. There are two types of nav items,
   * Root top level nav items and sub navigation items.
   *
   * `selectedRootNavItemId` is used to determine whether or not the collapsible
   * sub navigation menu is opened or closed based on external state such as
   * NextJS router browser history current route.
   */
  selectedRootNavItemId: string;
  isLoggedInViewingFundPlatform: boolean;
  hideHeader?: boolean;
  currentPath: string;
  location: Location;
  displayName: string | null;
  setUserLocation: (update: { countryCode: string }) => void;
  offsetTop?: number;
} & Omit<
  TopAppBarProps,
  | "onClickHomeIcon"
  | "onClickMenuIcon"
  | "isOpen"
  | "isHamburgerVisible"
  | "isLogoVisible"
>;

export function NavigationDrawer({
  currentPath,
  displayName,
  isLight,
  isLoggedInViewingFundPlatform,
  isOverlayNav,
  location,
  navSections,
  selectedRootNavItemId,
  setUserLocation,
  hideHeader,
  offsetTop,
  ...props
}: NavigationDrawerProps) {
  // max mobile width is one pixel less than tablet break
  const isMobile = useMedia("(max-width: 767px)");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [showChangeLocation, setShowChangeLocation] = useState(false);
  const [isCountryPopupVisible, setCountryPopupState] =
    useState<boolean>(false);
  // Used to determine whether the sub navigation collapsible group is open or closed
  const [_selectedRootNavItemId, setSelectedRootNavItemId] =
    useState<string>("");
  const isLeft = !isOverlayNav && !isMobile;

  useEffect(() => {
    setSelectedRootNavItemId(selectedRootNavItemId);
  }, [selectedRootNavItemId]);

  useEffect(() => {
    setShowChangeLocation(Boolean(location && !displayName));
  }, [location, displayName]);

  const _isOverlayNav = isMobile || isOverlayNav;

  const isFundPlatformLoading = isLoggedInViewingFundPlatform === undefined;
  const isHamburgerVisible = _isOverlayNav && !isFundPlatformLoading;

  const handleOnClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.currentTarget.blur();
    setIsOpen(false);
  };

  return (
    <Root id="__ecologi-root" data-testid="navigation-drawer">
      <Drawer
        displayName={displayName}
        isFocusTrapDisabled={isCountryPopupVisible}
        isMobile={isMobile}
        isOpen={isOpen}
        isOverlayNav={isOverlayNav}
        onClose={() => setIsOpen(false)}
      >
        <Navigation>
          {navSections.map((section, index) => (
            <Fragment key={section.map((item) => item.id).join("|")}>
              {section.map((item) => {
                if ("links" in item) {
                  return (
                    <NavDropdown
                      isOpen={_selectedRootNavItemId === item.id}
                      key={item.id}
                      label={item.label}
                      onClick={() => {
                        const isOpen = _selectedRootNavItemId === item.id;
                        setSelectedRootNavItemId(isOpen ? "" : item.id);
                      }}
                      tag={item.tag}
                    >
                      {item.links.map((link) => (
                        <NavLink
                          href={link.path}
                          isActive={[link.route, link.path].includes(
                            currentPath
                          )}
                          key={link.id}
                          onClick={handleOnClick}
                          tag={link.tag}
                        >
                          <Typography use="body1" as="p">
                            {link.label}
                          </Typography>
                        </NavLink>
                      ))}
                    </NavDropdown>
                  );
                }

                return (
                  <NavLink
                    key={item.id}
                    href={item.path}
                    rightSlot={item.rightSlot}
                    tag={item.tag}
                    isActive={[item.route, item.path].includes(currentPath)}
                    onClick={handleOnClick}
                    isOpenInNewTab={item.isOpenInNewTab}
                  >
                    <Typography use="body1" as="p">
                      {item.label}
                    </Typography>
                  </NavLink>
                );
              })}
              {index + 1 < navSections.length && (
                <NavItemDivider key={`divider-${index}`} />
              )}
            </Fragment>
          ))}
          {!displayName && <NavItemDivider />}
        </Navigation>
        {showChangeLocation && (
          <CountryWrapper>
            <CountrySelect
              isLight={isLight}
              href="#switch-region"
              onClick={(e) => {
                e.preventDefault();
                setCountryPopupState(true);
              }}
            >
              <ReactCountryFlag
                countryCode={location?.countryCode || ""}
                aria-label="Country select button"
                style={{
                  height: "20px",
                  width: "30px",
                  verticalAlign: "inherit",
                }}
                svg
              />
            </CountrySelect>
          </CountryWrapper>
        )}
        <Link href="/" title="Go to the Ecologi homepage">
          <Logo />
        </Link>
        <CountryChangeModal
          setUserLocation={setUserLocation}
          location={location}
          onClose={() => setCountryPopupState(false)}
          isOpen={isCountryPopupVisible}
        />
      </Drawer>
      <ContentContainer isLeft={isLeft}>
        <TopAppBar
          {...props}
          isLight={isLight}
          onClickHomeIcon={() => setIsOpen(() => false)}
          onClickMenuIcon={() => setIsOpen((isOpen) => !isOpen)}
          isOpen={isOpen}
          isHamburgerVisible={isHamburgerVisible}
          isLogoVisible={_isOverlayNav && !hideHeader}
          offsetTop={offsetTop}
        />
        <ContentContainerInner>{props.children}</ContentContainerInner>
      </ContentContainer>
    </Root>
  );
}
